import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Form, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  PAYMENT_METHODS,
  confirmRecurringExpense,
  deleteExpense,
  deleteExpenseCategory,
  getExpenseSummary,
  listDueRecurringExpenses,
  listExpenseCategories,
  listExpenses,
  listRecurringExpenses,
  repeatExistingExpense,
  saveExpense,
  saveExpenseCategory,
  skipRecurringExpense,
  stopRecurringExpense,
} from "../../../services/expenseApi";

// How a head repeats. AUTO posts the same amount every month by itself; CONFIRM lists each month
// as due so the actual amount (loan interest, say) is entered.
const RECURRENCE_OPTIONS = [
  { value: "NONE", label: "One-time (paid as it comes)" },
  { value: "AUTO", label: "Monthly - same amount (added automatically)" },
  { value: "CONFIRM", label: "Monthly - amount varies (confirm each month)" },
];
const recurrenceShort = (value) =>
  value === "AUTO" ? "Monthly · automatic" : value === "CONFIRM" ? "Monthly · confirm" : "One-time";

const toISO = (date) => {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const thisMonthRange = () => {
  const now = new Date();
  return { fromDate: toISO(startOfMonth(now)), toDate: toISO(now) };
};

const lastMonthRange = () => {
  const now = new Date();
  const anchor = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { fromDate: toISO(startOfMonth(anchor)), toDate: toISO(endOfMonth(anchor)) };
};

const lastDaysRange = (days) => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));
  return { fromDate: toISO(from), toDate: toISO(now) };
};

const yearRange = () => {
  const now = new Date();
  return { fromDate: toISO(new Date(now.getFullYear(), 0, 1)), toDate: toISO(now) };
};

const blankEntry = (category) => ({
  category: category || "",
  expenseDate: toISO(new Date()),
  vendor: "",
  description: "",
  amount: "",
  periodStart: "",
  periodEnd: "",
  paymentMethod: "UPI",
  referenceNumber: "",
  notes: "",
  repeatMonths: 1,
  // NONE / AUTO / CONFIRM for this entry; starts as its head's setting.
  repeatMode: "NONE",
  repeatUntil: "",
});

const blankCategory = () => ({ code: "", name: "", periodBased: true, sortOrder: 100, active: true, recurrence: "NONE" });

const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const label = (value) => String(value || "").replaceAll("_", " ");
const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

const dayLabel = (iso) => {
  if (!iso) return "-";
  const [year, month, day] = String(iso).split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const monthsBetween = (start, end) => {
  if (!start || !end) return 1;
  const [ys, ms] = start.split("-").map(Number);
  const [ye, me] = end.split("-").map(Number);
  return Math.max(1, (ye - ys) * 12 + (me - ms) + 1);
};

export default function ExpensesManager() {
  const [range, setRange] = useState(thisMonthRange);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [categoryModal, setCategoryModal] = useState(null);
  const [showCategories, setShowCategories] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recurring, setRecurring] = useState([]);
  const [dueItems, setDueItems] = useState([]);
  // Amount and date typed into each due row, keyed by schedule and month.
  const [dueDrafts, setDueDrafts] = useState({});
  const [showRecurring, setShowRecurring] = useState(false);
  const [dueBusy, setDueBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // First, so any automatic month it posts is already in the list below.
      const recurringRows = await listRecurringExpenses().catch(() => []);
      const [entries, totals, categoryRows, due] = await Promise.all([
        listExpenses({ ...range, category: categoryFilter }),
        getExpenseSummary({ ...range, category: categoryFilter }),
        listExpenseCategories(false),
        listDueRecurringExpenses().catch(() => []),
      ]);
      setRows(entries);
      setSummary(totals);
      setCategories(categoryRows);
      setRecurring(recurringRows);
      setDueItems(due);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load expenses"));
    } finally {
      setLoading(false);
    }
  }, [range, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const activeCategories = useMemo(() => categories.filter((row) => row.active !== false), [categories]);
  const categoryName = useCallback(
    (code) => categories.find((row) => row.code === code)?.name || code,
    [categories]);
  const selectedCategory = useMemo(
    () => categories.find((row) => row.code === editing?.category),
    [categories, editing]);

  const patch = (field, value) => setEditing((prev) => ({ ...prev, [field]: value }));
  const patchRange = (field, value) => setRange((prev) => ({ ...prev, [field]: value }));
  const recurrenceOf = useCallback(
    (code) => categories.find((row) => row.code === code)?.recurrence || "NONE",
    [categories]);

  // A new entry starts repeating the way its head does; the entry can then choose otherwise.
  const pickHead = (code) => setEditing((prev) => ({
    ...prev,
    category: code,
    repeatMode: prev.id ? "NONE" : recurrenceOf(code),
  }));

  const openNew = () => {
    const code = categoryFilter || activeCategories[0]?.code || "";
    setEditing({ ...blankEntry(code), repeatMode: recurrenceOf(code) });
  };

  const dueKey = (item) => `${item.recurringId}:${item.offset}`;
  const todayISO = toISO(new Date());
  const dueDraft = (item) => ({
    amount: String(item.lastAmount ?? ""),
    // Nothing can be recorded as paid in the future; an upcoming month defaults to today.
    date: item.dueDate > todayISO ? todayISO : item.dueDate,
    ...(dueDrafts[dueKey(item)] || {}),
  });
  const patchDue = (item, field, value) =>
    setDueDrafts((prev) => ({ ...prev, [dueKey(item)]: { ...dueDraft(item), [field]: value } }));

  const confirmDue = async (item) => {
    const draft = dueDraft(item);
    if (!draft.amount || Number(draft.amount) <= 0) {
      toast.error("Enter the amount paid this month");
      return;
    }
    setDueBusy(dueKey(item));
    try {
      await confirmRecurringExpense(item.recurringId, item.offset, {
        amount: Number(draft.amount),
        expenseDate: draft.date || null,
        paymentMethod: item.paymentMethod || null,
      });
      await load();
      toast.success(`${item.categoryName} for ${dayLabel(item.dueDate)} added`);
    } catch (error) {
      toast.error(errorMessage(error, "Could not add this month"));
    } finally {
      setDueBusy(null);
    }
  };

  const skipDue = async (item) => {
    if (!window.confirm(`Skip ${item.categoryName} (${label(item.vendor)}) due ${dayLabel(item.dueDate)}? No entry is added for this month.`)) return;
    setDueBusy(dueKey(item));
    try {
      await skipRecurringExpense(item.recurringId, item.offset);
      await load();
      toast.success("Month skipped");
    } catch (error) {
      toast.error(errorMessage(error, "Could not skip this month"));
    } finally {
      setDueBusy(null);
    }
  };

  const stopRecurring = async (row) => {
    if (!window.confirm(`Stop repeating ${row.categoryName} to ${label(row.vendor)}? Entries already added stay.`)) return;
    try {
      await stopRecurringExpense(row.id);
      await load();
      toast.success("Stopped - no more months will be added");
    } catch (error) {
      toast.error(errorMessage(error, "Could not stop it"));
    }
  };

  const save = async () => {
    if (!editing.category) {
      toast.error("Pick an expense head");
      return;
    }
    if (!editing.vendor.trim()) {
      toast.error("Enter who it was paid to");
      return;
    }
    if (!editing.amount || Number(editing.amount) <= 0) {
      toast.error("Enter an amount greater than zero");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        amount: Number(editing.amount),
        periodStart: editing.periodStart || null,
        periodEnd: editing.periodEnd || null,
      };
      delete payload.repeatMonths;
      delete payload.repeatMode;
      delete payload.repeatUntil;
      const repeatingNow = editing.repeatMode !== "NONE" && !editing.recurringExpenseId;
      const repeat = { everyMonth: true, until: editing.repeatUntil || null, mode: editing.repeatMode };
      const saved = await saveExpense(
        payload,
        repeatingNow ? 1 : Number(editing.repeatMonths || 1),
        repeatingNow && !editing.id ? repeat : null,
      );
      // An entry already on the ledger is saved first, then starts repeating from its own date.
      if (repeatingNow && editing.id) {
        await repeatExistingExpense(editing.id, repeat);
      }
      setEditing(null);
      await load();
      toast.success(repeatingNow
        ? editing.repeatMode === "AUTO"
          ? "Expense saved - it will be added automatically every month"
          : "Expense saved - each month will show under Due this month to confirm"
        : saved.length > 1 ? `${saved.length} entries saved` : "Expense saved");
    } catch (error) {
      toast.error(errorMessage(error, "Could not save the expense"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${money(row.amount)} paid to ${row.vendor} on ${dayLabel(row.expenseDate)}?`)) return;
    try {
      await deleteExpense(row.id);
      await load();
      toast.success("Entry deleted");
    } catch (error) {
      toast.error(errorMessage(error, "Could not delete the entry"));
    }
  };

  const saveCategory = async () => {
    setSaving(true);
    try {
      await saveExpenseCategory({ ...categoryModal, sortOrder: Number(categoryModal.sortOrder || 0) });
      setCategoryModal(null);
      await load();
      toast.success("Expense head saved");
    } catch (error) {
      toast.error(errorMessage(error, "Could not save the head"));
    } finally {
      setSaving(false);
    }
  };

  const removeCategory = async (row) => {
    if (!window.confirm(`Delete the "${row.name}" head?`)) return;
    try {
      await deleteExpenseCategory(row.id);
      await load();
      toast.success("Head deleted");
    } catch (error) {
      toast.error(errorMessage(error, "Could not delete the head"));
    }
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <div className="fw-semibold">Expenses</div>
          <div className="small text-muted">
            Every company cost in one ledger — advertising, rent, cloud bills, subscriptions.
            Payments covering a stretch of time carry the period they cover.
          </div>
        </div>
        <Button size="sm" onClick={openNew}>+ Expense</Button>
      </Card.Header>
      <Card.Body>
        <div className="row g-2 align-items-end mb-3">
          <div className="col-lg-2 col-md-4">
            <Form.Label className="small">From</Form.Label>
            <Form.Control size="sm" type="date" value={range.fromDate}
              onChange={(e) => patchRange("fromDate", e.target.value)} />
          </div>
          <div className="col-lg-2 col-md-4">
            <Form.Label className="small">To</Form.Label>
            <Form.Control size="sm" type="date" value={range.toDate}
              onChange={(e) => patchRange("toDate", e.target.value)} />
          </div>
          <div className="col-lg-3 col-md-4">
            <Form.Label className="small">Head</Form.Label>
            <Form.Select size="sm" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="">All heads</option>
              {activeCategories.map((row) => (
                <option key={row.code} value={row.code}>{row.name}</option>
              ))}
            </Form.Select>
          </div>
          <div className="col-lg-5 d-flex flex-wrap gap-2">
            <Button size="sm" variant="outline-secondary" onClick={() => setRange(thisMonthRange())}>This Month</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setRange(lastMonthRange())}>Last Month</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setRange(lastDaysRange(30))}>Last 30 Days</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setRange(yearRange())}>This Year</Button>
          </div>
        </div>

        {summary && (
          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <div className="border rounded p-2 h-100">
                <div className="small text-muted">Total spend</div>
                <div className="fs-5 fw-semibold">{money(summary.total)}</div>
                <div className="small text-muted">
                  {summary.entries} entries · {summary.daysInRange} days
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="border rounded p-2 h-100">
                <div className="small text-muted">By head</div>
                {summary.byCategory?.length ? summary.byCategory.slice(0, 5).map((item) => (
                  <div key={item.category} className="d-flex justify-content-between small">
                    <span>{item.categoryName}</span>
                    <span className="fw-semibold">{money(item.total)}</span>
                  </div>
                )) : <div className="small text-muted">Nothing recorded.</div>}
              </div>
            </div>
            <div className="col-md-4">
              <div className="border rounded p-2 h-100">
                <div className="small text-muted">By payee</div>
                {summary.byVendor?.length ? summary.byVendor.slice(0, 5).map((item) => (
                  <div key={item.vendor} className="d-flex justify-content-between small">
                    <span>{label(item.vendor)}</span>
                    <span className="fw-semibold">{money(item.total)}</span>
                  </div>
                )) : <div className="small text-muted">Nothing recorded.</div>}
              </div>
            </div>
          </div>
        )}

        {dueItems.length > 0 && (
          <div className="border border-warning rounded p-2 mb-3 bg-warning-subtle">
            <div className="fw-semibold small mb-1">
              Due this month - confirm the amount paid ({dueItems.length})
            </div>
            <div className="small text-muted mb-2">
              These repeat monthly but the amount changes, so each month is added only once you confirm it.
              The amount shown is last month's - change it to what was actually paid.
            </div>
            <div className="table-responsive">
              <Table size="sm" className="small align-middle mb-0 bg-white">
                <thead>
                  <tr>
                    <th>Due</th><th>Head</th><th>Paid to</th><th>Covers</th>
                    <th style={{ width: 130 }}>Amount (₹)</th><th style={{ width: 150 }}>Paid on</th><th />
                  </tr>
                </thead>
                <tbody>
                  {dueItems.map((item) => {
                    const draft = dueDraft(item);
                    const busy = dueBusy === dueKey(item);
                    return (
                      <tr key={dueKey(item)}>
                        <td className="text-nowrap">
                          {dayLabel(item.dueDate)}
                          {item.overdue && <Badge bg="danger" className="ms-1">Overdue</Badge>}
                        </td>
                        <td>{item.categoryName}</td>
                        <td>
                          {label(item.vendor)}
                          {item.description && <div className="text-muted">{item.description}</div>}
                        </td>
                        <td className="text-nowrap">
                          {item.periodStart && item.periodEnd ? `${dayLabel(item.periodStart)} – ${dayLabel(item.periodEnd)}` : "-"}
                        </td>
                        <td>
                          <Form.Control size="sm" type="number" min="0" step="0.01" value={draft.amount}
                            onChange={(e) => patchDue(item, "amount", e.target.value)} />
                        </td>
                        <td>
                          <Form.Control size="sm" type="date" max={todayISO} value={draft.date}
                            onChange={(e) => patchDue(item, "date", e.target.value)} />
                        </td>
                        <td className="text-nowrap">
                          <Button size="sm" className="me-1" disabled={busy} onClick={() => confirmDue(item)}>
                            {busy ? <Spinner size="sm" /> : "Add"}
                          </Button>
                          <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => skipDue(item)}>
                            Skip
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-muted"><Spinner size="sm" className="me-2" />Loading expenses...</div>
        ) : (
          <div className="table-responsive">
            <Table bordered hover size="sm" className="align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: 44 }}>#</th>
                  <th>Date</th>
                  <th>Head</th>
                  <th>Paid to</th>
                  <th>Description</th>
                  <th>Covers</th>
                  <th className="text-end">Amount</th>
                  <th style={{ width: 140 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const months = row.periodStart && row.periodEnd
                    ? monthsBetween(row.periodStart.slice(0, 7), row.periodEnd.slice(0, 7))
                    : null;
                  return (
                    <tr key={row.id}>
                      <td className="small fw-semibold">{index + 1}</td>
                      <td className="fw-semibold">
                        {dayLabel(row.expenseDate)}
                        {row.paymentMethod && <div className="small text-muted">{label(row.paymentMethod)}</div>}
                      </td>
                      <td>
                        <Badge bg="secondary">{categoryName(row.category)}</Badge>
                        {row.recurringExpenseId && (
                          <div><Badge bg="info" text="dark" className="mt-1">Recurring</Badge></div>
                        )}
                      </td>
                      <td>{label(row.vendor)}</td>
                      <td>
                        {row.description || "-"}
                        {row.referenceNumber && <div className="small text-muted">{row.referenceNumber}</div>}
                      </td>
                      <td className="small">
                        {months ? (
                          <>
                            {dayLabel(row.periodStart)} – {dayLabel(row.periodEnd)}
                            <div className="text-muted">
                              {months} months · {money(Number(row.amount || 0) / months)}/month
                            </div>
                          </>
                        ) : <span className="text-muted">-</span>}
                      </td>
                      <td className="text-end fw-semibold">{money(row.amount)}</td>
                      <td className="d-flex gap-1">
                        <Button size="sm" variant="outline-primary"
                          onClick={() => setEditing({
                            ...blankEntry(row.category), ...row,
                            amount: String(row.amount ?? ""),
                            periodStart: row.periodStart || "",
                            periodEnd: row.periodEnd || "",
                            repeatMonths: 1,
                          })}>
                          Edit
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => remove(row)}>Delete</Button>
                      </td>
                    </tr>
                  );
                })}
                {!rows.length && (
                  <tr><td colSpan={8} className="text-center text-muted">No expenses recorded for this range.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        )}

        <hr className="my-4" />

        <div role="button" className="d-flex align-items-center gap-2" onClick={() => setShowRecurring((v) => !v)}>
          <i className={`bi ${showRecurring ? "bi-chevron-down" : "bi-chevron-right"}`} aria-hidden="true" />
          <div>
            <div className="fw-semibold">Recurring Expenses ({recurring.length})</div>
            <div className="small text-muted">
              What repeats every month. Set one up by adding an expense with "Repeats" set to
              monthly - any head, the head only sets the default. Stopping keeps the entries already added.
            </div>
          </div>
        </div>
        {showRecurring && (
          <div className="table-responsive mt-3">
            <Table bordered hover size="sm" className="small mb-0 align-middle">
              <thead>
                <tr>
                  <th>Head</th><th>Paid to</th><th>How</th><th className="text-end">Amount</th>
                  <th>Started</th><th>Next</th><th>Until</th><th />
                </tr>
              </thead>
              <tbody>
                {recurring.map((row) => (
                  <tr key={row.id}>
                    <td className="fw-semibold">{row.categoryName}</td>
                    <td>
                      {label(row.vendor)}
                      {row.description && <div className="text-muted">{row.description}</div>}
                    </td>
                    <td>
                      <Badge bg={row.mode === "AUTO" ? "success" : "warning"} text={row.mode === "AUTO" ? undefined : "dark"}>
                        {row.mode === "AUTO" ? "Added automatically" : "Confirm each month"}
                      </Badge>
                    </td>
                    <td className="text-end">
                      {money(row.amount)}
                      {row.mode === "CONFIRM" && <div className="text-muted">last paid</div>}
                    </td>
                    <td>{dayLabel(row.firstDate)}</td>
                    <td>{dayLabel(row.nextDueDate)}</td>
                    <td>{row.endDate ? dayLabel(row.endDate) : "Until stopped"}</td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-danger" onClick={() => stopRecurring(row)}>Stop</Button>
                    </td>
                  </tr>
                ))}
                {!recurring.length && (
                  <tr><td colSpan={8} className="text-center text-muted">Nothing repeats monthly yet.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        )}

        <hr className="my-4" />

        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div role="button" className="d-flex align-items-center gap-2" onClick={() => setShowCategories((v) => !v)}>
            <i className={`bi ${showCategories ? "bi-chevron-down" : "bi-chevron-right"}`} aria-hidden="true" />
            <div>
              <div className="fw-semibold">Expense Heads ({categories.length})</div>
              <div className="small text-muted">
                The list expenses are filed under. Add a head here rather than typing a new one each time.
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline-secondary" onClick={() => setCategoryModal(blankCategory())}>+ Head</Button>
        </div>

        {showCategories && (
          <div className="table-responsive mt-3">
            <Table bordered hover size="sm" className="small mb-0 align-middle">
              <thead>
                <tr>
                  <th>Name</th><th>Code</th><th>Repeats</th><th>Usually a period</th><th>Order</th><th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((row) => (
                  <tr key={row.id}>
                    <td className="fw-semibold">{row.name}</td>
                    <td className="text-muted">{row.code}</td>
                    <td>{recurrenceShort(row.recurrence)}</td>
                    <td>{row.periodBased ? "Yes" : "No"}</td>
                    <td>{row.sortOrder}</td>
                    <td>
                      <Badge bg={row.active === false ? "secondary" : "success"}>
                        {row.active === false ? "Inactive" : "Active"}
                      </Badge>
                    </td>
                    <td className="text-end">
                      <div className="d-flex gap-1 justify-content-end">
                        <Button size="sm" variant="outline-primary" onClick={() => setCategoryModal({ ...row })}>Edit</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => removeCategory(row)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!categories.length && (
                  <tr><td colSpan={7} className="text-center text-muted">No heads configured.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>

      <Modal show={!!editing} onHide={() => !saving && setEditing(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="h6">{editing?.id ? "Edit Expense" : "Add Expense"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editing && (
            <div className="row g-2">
              <div className="col-md-4">
                <Form.Label>Head *</Form.Label>
                <Form.Select value={editing.category} onChange={(e) => pickHead(e.target.value)}>
                  <option value="">Select head</option>
                  {activeCategories.map((row) => (
                    <option key={row.code} value={row.code}>{row.name}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-4">
                <Form.Label>Paid to *</Form.Label>
                <Form.Control value={editing.vendor} placeholder="Meta, Airtel, AWS, landlord"
                  onChange={(e) => patch("vendor", e.target.value)} />
              </div>
              <div className="col-md-4">
                <Form.Label>Amount (₹) *</Form.Label>
                <Form.Control type="number" min="0" step="0.01" value={editing.amount}
                  onChange={(e) => patch("amount", e.target.value)} />
              </div>
              <div className="col-md-4">
                <Form.Label>Payment date *</Form.Label>
                <Form.Control type="date" max={toISO(new Date())} value={editing.expenseDate || ""}
                  onChange={(e) => patch("expenseDate", e.target.value)} />
              </div>
              <div className="col-md-4">
                <Form.Label>Paid by</Form.Label>
                <Form.Select value={editing.paymentMethod || "UPI"} onChange={(e) => patch("paymentMethod", e.target.value)}>
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method} value={method}>{label(method)}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-4">
                <Form.Label>Reference / invoice</Form.Label>
                <Form.Control value={editing.referenceNumber || ""}
                  onChange={(e) => patch("referenceNumber", e.target.value)} />
              </div>
              <div className="col-md-8">
                <Form.Label>Description</Form.Label>
                <Form.Control value={editing.description || ""}
                  placeholder="Campaign name, what was bought, invoice subject"
                  onChange={(e) => patch("description", e.target.value)} />
                <Form.Text className="text-muted">
                  Also what keeps two entries on the same day for the same payee apart.
                </Form.Text>
              </div>
              <div className="col-md-4">
                <Form.Label>Add past months too</Form.Label>
                <Form.Control type="number" min="1" max="36"
                  disabled={!!editing.id || editing.repeatMode !== "NONE"}
                  value={editing.repeatMonths}
                  onChange={(e) => patch("repeatMonths", e.target.value)} />
                <Form.Text className="text-muted">
                  {editing.id
                    ? "Not available when editing."
                    : "Back-fill only: N entries, one per month from the payment date, never past today."}
                </Form.Text>
              </div>

              {editing.recurringExpenseId ? (
                <div className="col-12">
                  <div className="border rounded p-2 small text-muted">
                    <Badge bg="info" text="dark" className="me-2">Recurring</Badge>
                    This expense already repeats monthly. Stop or check it under Recurring Expenses.
                  </div>
                </div>
              ) : (
                <div className="col-12">
                  <div className={`border rounded p-2${editing.repeatMode !== "NONE" ? " border-info" : ""}`}>
                    <div className="row g-2 align-items-end">
                      <div className="col-md-6">
                        <Form.Label className="small">Repeats</Form.Label>
                        <Form.Select size="sm" value={editing.repeatMode || "NONE"}
                          onChange={(e) => patch("repeatMode", e.target.value)}>
                          {RECURRENCE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </Form.Select>
                      </div>
                      <div className="col-md-6 small text-muted">
                        {editing.id
                          ? "Pick monthly to repeat this expense from its payment date. This entry stays as it is."
                          : editing.category
                          ? `Head default: ${recurrenceShort(recurrenceOf(editing.category))}. This entry can differ.`
                          : "Pick a head first."}
                      </div>
                    </div>
                    {editing.repeatMode !== "NONE" && (
                      <div className="row g-2 mt-1 align-items-end">
                        <div className="col-md-4">
                          <Form.Label className="small">Until (optional)</Form.Label>
                          <Form.Control type="date" min={editing.expenseDate || undefined} value={editing.repeatUntil || ""}
                            onChange={(e) => patch("repeatUntil", e.target.value)} />
                        </div>
                        <div className="col-md-8 small text-muted">
                          {editing.repeatUntil
                            ? `Last month added: the one on or before ${dayLabel(editing.repeatUntil)}.`
                            : "Repeats until you stop it under Recurring Expenses."}
                          {" "}Tip: leave the month out of the description - it repeats as written.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="col-12">
                <div className="border rounded p-2 bg-light">
                  <div className="fw-semibold small mb-1">
                    Period covered {selectedCategory?.periodBased ? "" : "(optional)"}
                  </div>
                  <div className="small text-muted mb-2">
                    Fill this when one payment covers a stretch of time — an extension bought for
                    three months, a year of hosting. Leave blank for a one-off cost.
                  </div>
                  <div className="row g-2">
                    <div className="col-md-4">
                      <Form.Label className="small">From</Form.Label>
                      <Form.Control type="date" value={editing.periodStart || ""}
                        onChange={(e) => patch("periodStart", e.target.value)} />
                    </div>
                    <div className="col-md-4">
                      <Form.Label className="small">To</Form.Label>
                      <Form.Control type="date" value={editing.periodEnd || ""}
                        onChange={(e) => patch("periodEnd", e.target.value)} />
                    </div>
                    <div className="col-md-4 d-flex align-items-end">
                      {editing.periodStart && editing.periodEnd && Number(editing.amount) > 0 && (
                        <div className="small text-muted">
                          {monthsBetween(editing.periodStart.slice(0, 7), editing.periodEnd.slice(0, 7))} months ·{" "}
                          {money(Number(editing.amount)
                            / monthsBetween(editing.periodStart.slice(0, 7), editing.periodEnd.slice(0, 7)))} per month
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <Form.Label>Notes</Form.Label>
                <Form.Control value={editing.notes || ""} onChange={(e) => patch("notes", e.target.value)} />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={saving} onClick={() => setEditing(null)}>Cancel</Button>
          <Button disabled={saving} onClick={save}>{saving ? "Saving..." : "Save Expense"}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!categoryModal} onHide={() => !saving && setCategoryModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">{categoryModal?.id ? "Edit Head" : "New Head"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {categoryModal && (
            <div className="row g-2">
              <div className="col-12">
                <Form.Label>Name *</Form.Label>
                <Form.Control value={categoryModal.name} placeholder="e.g. Laptop Rent"
                  onChange={(e) => setCategoryModal((old) => ({ ...old, name: e.target.value }))} />
              </div>
              <div className="col-6">
                <Form.Label>Code *</Form.Label>
                <Form.Control value={categoryModal.code} placeholder="LAPTOP_RENT" disabled={!!categoryModal.id}
                  onChange={(e) => setCategoryModal((old) => ({ ...old, code: e.target.value }))} />
                <Form.Text className="text-muted">
                  {categoryModal.id ? "The code is fixed once entries use it." : "A short stable key. The name can be renamed later."}
                </Form.Text>
              </div>
              <div className="col-6">
                <Form.Label>Display order</Form.Label>
                <Form.Control type="number" value={categoryModal.sortOrder}
                  onChange={(e) => setCategoryModal((old) => ({ ...old, sortOrder: e.target.value }))} />
              </div>
              <div className="col-12">
                <Form.Label>Repeats</Form.Label>
                <Form.Select value={categoryModal.recurrence || "NONE"}
                  onChange={(e) => setCategoryModal((old) => ({ ...old, recurrence: e.target.value }))}>
                  {RECURRENCE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Travel, advertising: one-time. Rent, fixed subscriptions: same amount. Loan interest,
                  bills that change: confirm each month. Only affects entries set up from now on.
                </Form.Text>
              </div>
              <div className="col-12 d-flex gap-4 mt-2">
                <Form.Check type="switch" label="Usually covers a period"
                  checked={categoryModal.periodBased !== false}
                  onChange={(e) => setCategoryModal((old) => ({ ...old, periodBased: e.target.checked }))} />
                <Form.Check type="switch" label="Active"
                  checked={categoryModal.active !== false}
                  onChange={(e) => setCategoryModal((old) => ({ ...old, active: e.target.checked }))} />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={saving} onClick={() => setCategoryModal(null)}>Cancel</Button>
          <Button disabled={saving} onClick={saveCategory}>{saving ? "Saving..." : "Save Head"}</Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
